import {Input, Spin} from 'antd'
import {
    report_getAuthErrorReports_Api,
    report_getDefaultReasons_Api,
    report_getReportsByOwner_Api,
    report_getReportsByType_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {LoginContext} from 'components/LoginContext'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import filterOption from 'helpers/filterOption'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {UseConfirmDeleteAction} from 'views/Document/CodiDocumentContext'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {ATable, TruncatedCell} from 'views/AppLocals/EntityList'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {successNotify} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {getResponseItems, NullAsDefault, renderIf} from 'views/Shared'
import ErrorReportContext, {
    ErrorReportActionTypes,
    ErrorReportProvider,
    ErrorReportStatus,
    getErrorReportActions,
} from './ErrorReportContext'
import {renderReportStatus} from './functions/renderReportStatus'
import getLinkToDetail from '../../helpers/getLinkToDetail'
import {Link} from 'react-router-dom'
import {useRouteSearchParam} from "../../envs/_current/history";
import TooltipApp from "components/TooltipApp";

const ReportActions = ({item, actions, handleAction = NullAsDefault}) => {
  const t = useTranslate()

  const array = Array.from(actions ?? [])

  return renderIf(
    array.length,
    <div className="flex flex-center gap-3">
      {array.map((action, index) => {
        const {
          value,
          label,
          style,
          className,
          Icon = NullAsDefault,
          confirm = false,
        } = action ?? {}

        const {
          notify = (callback) => {
          },
          withItem = (item) => [,],
          ...rest
        } = confirm ?? {}

        const Component = (props) => (
          <TooltipApp title={t(label)}>
            <Icon
              size={17}
              style={style}
              className={classNames('cursor-pointer', className)}
              {...props}
            />
          </TooltipApp>
        )

        return (
          <React.Fragment key={index}>
            {confirm ? (
              <UseConfirmDeleteAction
                onError={notifyOnError(t)}
                onSuccess={() => {
                  notify((type) => successNotify(type, t))
                  handleAction(ErrorReportActionTypes.REFRESH.value)
                }}
                {...rest}>
                {({isLoading = false, confirm = NullAsDefault}) => (
                  <Spin
                    wrapperClassName="custom-spin"
                    spinning={!!isLoading}>
                    <Component
                      onClick={() => {
                        const [values, query] = withItem(item) ?? [,]
                        confirm(values, query)
                      }}
                    />
                  </Spin>
                )}
              </UseConfirmDeleteAction>
            ) : (
              <Component
                onClick={() => {
                  handleAction(value, item)
                }}
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export const ErrorReportsAdvancedFilter = ({
                                             style,
                                             className,
                                             value: filter = {
                                               status: undefined,
                                               report_default_reason_id: undefined,
                                             },
                                             onChange: setValues = NullAsDefault,
                                           }) => {
  const t = useTranslate()

  const {owner_type} = useContext(ErrorReportContext)

  const apiInfo = report_getDefaultReasons_Api

  const cacheId = apiInfo.path.replace(':type', owner_type)

  const {response} = useAsyncWithCache({
    cacheId,
    apiInfo,
    query: bindQueryParam({type: owner_type}),
  })

  const reason_options = useMemo(() => {
    const items = getResponseItems(response)
    return Array.from(items).map((item) => ({
      label: getTitle(item),
      value: getId(item),
      ...item,
    }))
  }, [response])

  const status_options = useMemo(
    () =>
      Object.values(ErrorReportStatus).map(({label, ...rest}) => ({
        label: t(label),
        ...rest,
      })),
    []
  )

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  return (
    <AntdConfigWrapper>
      <div
        style={style}
        className={classNames(
          'grid grid-cols-1 md:grid-cols-2 gap-3',
          className
        )}>
        <div className="flex items-center gap-3">
          <div className="text-sm text-color-300 italic whitespace-no-wrap">
            {`${t('reason')} : `}
          </div>
          <SelectWidget
            showSearch={true}
            allowClear={true}
            options={reason_options}
            optionFilterProp="label"
            placeholder={t('choose reason')}
            filterOption={filterOption}
            className="CenterSelector w-full"
            onChange={(value) => {
              handleChange('report_default_reason_id', value)
            }}
            value={
              _.find(reason_options, {value: filter.report_default_reason_id})
                ? filter.report_default_reason_id
                : undefined
            }
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-color-300 italic whitespace-no-wrap">
            {`${t('status')} : `}
          </div>
          <SelectWidget
            showSearch={true}
            allowClear={true}
            options={status_options}
            optionFilterProp="label"
            placeholder={t('status')}
            filterOption={filterOption}
            className="CenterSelector w-full"
            onChange={(value) => {
              handleChange('status', value)
            }}
            value={
              _.find(status_options, {value: filter.status})
                ? filter.status
                : undefined
            }
          />
        </div>
      </div>
    </AntdConfigWrapper>
  )
}

const ErrorReportManager = ({query, values, apiInfo, Wrapper = 'div'}) => {
  const t = useTranslate()

  const {isSm} = useContext(LayoutContext)

  const [keyword, setKeyword] = useState()

  const [filter, setFilter] = useState({})

  const onSearch = _.debounce((keyword) => {
    setKeyword(keyword)
  }, 500)

  const columns = [
    {
      width: 200,
      title: 'report reason',
      dataIndex: 'report_reason',
      render: (value) => (
        <span className="font-semibold text-sm text-color-000">
          {getTitle(value)}
        </span>
      ),
    },
    {
      title: 'title',
      dataIndex: 'title',
      render: (value, {description, owner}) => (
        <TruncatedCell tooltip={getTitle(owner)}>
          <div className="flex flex-col">
            <Link
              to={getLinkToDetail(owner)}
              className="font-bold text-sm text-primary">
              {getTitle(owner)}
            </Link>
            <span className="font-bold text-doc-xs text-color-100 italic mt-1">
              {value}
            </span>
            <span className="text-xs text-color-500 italic leading-tight">
              {description}
            </span>
          </div>
        </TruncatedCell>
      ),
    },
    {
      width: 200,
      title: 'notes',
      dataIndex: 'notes',
      render: (value) => (
        <TruncatedCell
          maxWidth={200}
          tooltip={value}>
          {value}
        </TruncatedCell>
      ),
    },
    {
      width: 100,
      title: 'status',
      align: 'center',
      dataIndex: 'status',
      render: renderReportStatus,
    },
    {
      width: 100,
      title: 'action',
      align: 'center',
      dataIndex: 'action',
      render: (__, record) => (
        <ErrorReportContext.Consumer>
          {({handleAction}) => (
            <ReportActions
              item={record}
              handleAction={handleAction}
              actions={getErrorReportActions(record?.status)}
            />
          )}
        </ErrorReportContext.Consumer>
      ),
    },
  ].map(({title, ...rest}) => ({
    title: (
      <span className="font-semibold text-sm whitespace-no-wrap">
        {t(title)}
      </span>
    ),
    ...rest,
  }))

  return (
    <Wrapper className="flex flex-col container mx-auto gap-3 p-3">
      <div className="flex flex-col items-center mb-6">
        <span className="font-medium text-sm text-color-400 uppercase tracking-wider leading-tight">
          {t('feedback on document errors')}
        </span>
        <div className="font-bold text-lg md:text-2xl text-color-000 uppercase tracking-wider">
          {t('reports manager')}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Input.Search
          name="keyword"
          autoComplete="off"
          allowClear={true}
          onChange={(event) => {
            onSearch(event?.target?.value)
          }}
          onSearch={onSearch}
          placeholder={t('keyword')}
          className="input-search-with-icon"
        />
        <ErrorReportsAdvancedFilter
          onChange={(values) => {
            setFilter(values)
          }}
          value={filter}
        />
      </div>
      <ErrorReportContext.Consumer>
        {({modal, refreshToken}) => (
          <React.Fragment>
            <div className="flex flex-col gap-2">
              <ATable
                size="small"
                bordered={false}
                columns={columns}
                apiInfo={apiInfo}
                createQuery={() => query}
                rowClassName="bordered-cell"
                rowKey={(record) => getId(record)}
                dependencies={[query, values, refreshToken]}
                createValues={({pagination}) => ({
                  ...(values ?? {}),
                  ...filter,
                  keyword,
                  page: _.defaultTo(pagination?.current, 1),
                })}
                scroll={isSm ? {x: 'max-content'} : undefined}
                className="custom-out-table border-collapse-table"
              />
            </div>
            {modal ?? null}
          </React.Fragment>
        )}
      </ErrorReportContext.Consumer>
    </Wrapper>
  )
}

export const ErrorReportList = ({
                                  style,
                                  query,
                                  values,
                                  sortBy,
                                  filters,
                                  apiInfo,
                                  keyword,
                                  className,
                                  renderItem,
                                  filter_actions,
                                  AdvancedFilter,
                                  withPager = () => ({}),
                                  ...props
                                }) => {
  const {modal, spinning, refreshToken} = useContext(ErrorReportContext)

  const refresh_deps = useMemo(
    () => [refreshToken, JSON.stringify(query), JSON.stringify(values)],
    [query, values, refreshToken]
  )

  const [pager_widget] = usePaginationHelper({
    ...props,
    AdvancedFilter,
    renderSortBy: NullAsDefault,
    SortByWidget: NullAsDefault,
    isToggleDefault: true,
    AdvancedToggle: NullAsDefault,
    renderFilter: AdvancedFilter ? undefined : NullAsDefault,
    pagerInfo: {
      query,
      apiInfo,
      renderItem,
      RenderEntity: React.Fragment,
      ...(withPager() ?? {}),
    },
    query_params: filter_actions,
    withValues: (rest) => ({
      ...(rest ?? {}),
      ...(values ?? {}),
    }),
    withKey: createPagerKey(refresh_deps),
    onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
      if (sortBy) {
        onSortChange(sortBy)
      }
      if (filters) {
        onFilterChange(filters)
      }
    },
  })

  return (
    <Spin spinning={!!spinning}>
      <div
        style={style}
        className={className}>
        {pager_widget}
        {modal ?? null}
      </div>
    </Spin>
  )
}

const ContentRenderer = ({modal, children}) => {
  if (modal) {
    return (
      <FullSideModalLayout {...(modal ?? {})}>
        <DelayRender lazy={true}>{children}</DelayRender>
      </FullSideModalLayout>
    )
  } else {
    return children ?? null
  }
}

const EnhancedReportManager = ({
                                 owner_id,
                                 owner_type,
                                 Renderer = ErrorReportManager,
                                 ...props
                               }) => (
  <ErrorReportProvider
    owner_id={owner_id}
    owner_type={owner_type}>
    <Renderer {...props} />
  </ErrorReportProvider>
)

export const CodiReportManager = ({id, type, modal, ...props}) =>
  renderIf(
    _.every([id, type], Boolean),
    <ContentRenderer modal={modal}>
      <EnhancedReportManager
        {...props}
        owner_id={id}
        owner_type={type}
        query={{
          id,
          type,
        }}
        apiInfo={report_getReportsByOwner_Api}
      />
    </ContentRenderer>
  )

export const EntityReportManager = ({type, modal, ...props}) =>
  renderIf(
    type,
    <ContentRenderer modal={modal}>
      <EnhancedReportManager
        {...props}
        owner_type={type}
        query={{
          type,
        }}
        apiInfo={report_getReportsByType_Api}
      />
    </ContentRenderer>
  )

export const CodiReportList = ({id, type, modal, ...props}) => {
  const filter_actions = {
    sortBy: 'sort-by',
    keyword: 'keyword',
    status: 'status',
    report_default_reason_id: 'reason',
  }

  const sortBy = useRouteSearchParam(filter_actions.sortBy)

  const status = useRouteSearchParam(filter_actions.status)

  const report_default_reason_id = useRouteSearchParam(
    filter_actions.report_default_reason_id
  )

  return renderIf(
    _.every([id, type], Boolean),
    <ContentRenderer modal={modal}>
      <ErrorReportList
        sortBy={sortBy}
        filters={{
          status,
          report_default_reason_id,
        }}
        filter_actions={filter_actions}
        apiInfo={report_getReportsByOwner_Api}
        query={bindQueryParams([{id}, {type}])}
        {...props}
      />
    </ContentRenderer>
  )
}

export const AuthReportList = ({modal, ...props}) => {
  const filter_actions = {
    sortBy: 'sort-by',
    keyword: 'keyword',
    status: 'status',
    report_default_reason_id: 'reason',
  }

  const login = useContext(LoginContext)

  const sortBy = useRouteSearchParam(filter_actions.sortBy)

  const status = useRouteSearchParam(filter_actions.status)

  const report_default_reason_id = useRouteSearchParam(
    filter_actions.report_default_reason_id
  )

  return renderIf(
    login,
    <ContentRenderer modal={modal}>
      <ErrorReportList
        sortBy={sortBy}
        filters={{
          status,
          report_default_reason_id,
        }}
        filter_actions={filter_actions}
        apiInfo={report_getAuthErrorReports_Api}
        {...props}
      />
    </ContentRenderer>
  )
}

export default EnhancedReportManager
